import React from 'react';
import { Checkbox, Col, DatePicker, Form, Input, Row, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import type { FormInstance } from 'antd';
import CheckAllButtons from '../CheckAllButtons';
import WeldMetaBlock from '../WeldMetaBlock';
import { SECTION_HEADER_WIDTH } from '../types';
import type { WeldMeta } from '../types';
import Button from '../../../../../../../../components/common/Button';

const { Text } = Typography;

export interface VcPanelProps {
  form: FormInstance;
  weldKey: string;
  isView: boolean;
  meta?: WeldMeta;
  onOpenWelderSelect: (layer: 'flw' | 'fw') => void;
}

/** VC (Visual Check) detay paneli — JSX PipeInspectionForm'dan birebir taşındı.
 *  WelderSelect modalı orkestratörde; kaynakçı inputları form store'a doğrudan
 *  bağlıdır (Space.Compact > Form.Item noStyle). FLW/FW tarih senkronu panelde. */
export default function VcPanel({ form, weldKey, isView, meta, onOpenWelderSelect }: VcPanelProps) {
  return (
    <div>
      {/* Weld meta info from RFI (read-only) */}
      <WeldMetaBlock meta={meta} />
      <div style={{ padding: 4, backgroundColor: '#e6f4ff', borderRadius: 4 }}>
        <Row gutter={[6, 4]}>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Welders</Text>
              <Text>FLW</Text>
              <Space.Compact>
                <Form.Item name={['results', weldKey, 'vc', 'flw_welder']} noStyle>
                  <Input size="small" style={{ width: 200 }} readOnly />
                </Form.Item>
                {!isView && (
                  <Button onMouseDown={(e) => e.preventDefault()} onClick={() => onOpenWelderSelect('flw')}>Select</Button>
                )}
              </Space.Compact>
              <Text>FW</Text>
              <Space.Compact>
                <Form.Item name={['results', weldKey, 'vc', 'fw_welder']} noStyle>
                  <Input size="small" style={{ width: 200 }} readOnly />
                </Form.Item>
                {!isView && (
                  <Button onMouseDown={(e) => e.preventDefault()} onClick={() => onOpenWelderSelect('fw')}>Select</Button>
                )}
              </Space.Compact>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Weld Dates</Text>
              <Text>FLW</Text>
              <Form.Item name={['results', weldKey, 'vc', 'flw_date']} style={{ margin: 0 }}>
                <DatePicker
                  size="small"
                  format="YYYY-MM-DD"
                  disabled={isView}
                  disabledDate={(current) => {
                    const rd = form.getFieldValue('report_date');
                    const report = rd && typeof rd?.format === 'function' ? rd : (rd ? dayjs(rd) : null);
                    const fw = form.getFieldValue(['results', weldKey, 'vc', 'fw_date']);
                    const fwD = fw && typeof fw?.format === 'function' ? fw : (fw ? dayjs(fw) : null);
                    const afterReport = report ? (current && current.isAfter(report.endOf('day'))) : false;
                    const afterFw = fwD ? (current && current.isAfter(fwD.endOf('day'))) : false;
                    return afterReport || afterFw;
                  }}
                  onChange={(d) => {
                    // FW boşsa ya da FLW'den önceyse FW'yi FLW'ye eşitle (form store tek kaynak;
                    // payload senkronu useInspectionResultsSync + submit overlay'inde)
                    const fwCur = form.getFieldValue(['results', weldKey, 'vc', 'fw_date']);
                    const fwDay = fwCur && typeof fwCur?.format === 'function' ? fwCur : (fwCur ? dayjs(fwCur) : null);
                    if (!fwDay || (d && fwDay.isBefore(d, 'day'))) {
                      form.setFields([{ name: ['results', weldKey, 'vc', 'fw_date'], value: d || null }]);
                    }
                  }}
                />
              </Form.Item>
              <Text>FW</Text>
              <Form.Item name={['results', weldKey, 'vc', 'fw_date']} style={{ margin: 0 }}>
                <DatePicker
                  size="small"
                  format="YYYY-MM-DD"
                  disabled={isView}
                  disabledDate={(current) => {
                    const rd = form.getFieldValue('report_date');
                    const report = rd && typeof rd?.format === 'function' ? rd : (rd ? dayjs(rd) : null);
                    const flw = form.getFieldValue(['results', weldKey, 'vc', 'flw_date']);
                    const flwD = flw && typeof flw?.format === 'function' ? flw : (flw ? dayjs(flw) : null);
                    const afterReport = report ? (current && current.isAfter(report.endOf('day'))) : false;
                    const beforeFlw = flwD ? (current && current.isBefore(flwD.startOf('day'))) : false;
                    return afterReport || beforeFlw;
                  }}
                  onChange={(d) => {
                    // Ensure FLW <= FW; if FLW missing or after, set FLW = FW
                    const flwCur = form.getFieldValue(['results', weldKey, 'vc', 'flw_date']);
                    const flwDay = flwCur && typeof flwCur?.format === 'function' ? flwCur : (flwCur ? dayjs(flwCur) : null);
                    if (d && (!flwDay || flwDay.isAfter(d, 'day'))) {
                      form.setFields([{ name: ['results', weldKey, 'vc', 'flw_date'], value: d }]);
                    }
                  }}
                />
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>External</Text>
              <Form.Item name={['results', weldKey, 'vc', 'external_profile_dimensions', 'weld_size_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Weld size ok</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'external_profile_dimensions', 'reinforcement_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Reinforcement ok</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'external_profile_dimensions', 'contour_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Contour/profile ok</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'external_profile_dimensions', 'undersize_oversize_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Undersize/oversize ok</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Surface</Text>
              <Form.Item name={['results', weldKey, 'vc', 'surface_discontinuities', 'no_cracks']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No cracks</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'surface_discontinuities', 'no_porosity']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No porosity</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'surface_discontinuities', 'undercut_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Undercut acceptable</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'surface_discontinuities', 'no_overlap']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No Overlap</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'surface_discontinuities', 'fusion_fill_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Fusion/fill ok</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Root</Text>
              <Form.Item name={['results', weldKey, 'vc', 'root_penetration', 'root_condition_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Root condition ok</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'root_penetration', 'penetration_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Penetration ok</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'root_penetration', 'root_reinforcement_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Root reinforcement/tie-in OK</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Cleanliness</Text>
              <Form.Item name={['results', weldKey, 'vc', 'cleanliness_contaminants', 'slag_spatter_removed']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Slag/spatter removed</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'cleanliness_contaminants', 'no_foreign_inclusions']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No foreign inclusions</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'cleanliness_contaminants', 'toes_adjacent_clean']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Toes/adjacent areas clean</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Thermal Eff.</Text>
              <Form.Item name={['results', weldKey, 'vc', 'haz_thermal_effects', 'discoloration_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Acceptable Discoloration</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'haz_thermal_effects', 'no_haz_cracking']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No HAZ cracking/crazing</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'haz_thermal_effects', 'coating_damage_checked']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Coating damage checked</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Distortion</Text>
              <Form.Item name={['results', weldKey, 'vc', 'distortion_alignment', 'misalignment_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Misalignment within tolerance</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'distortion_alignment', 'no_twist_bow']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No twist/bow affecting fit</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'distortion_alignment', 'dimensional_checks_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Dimensional checks</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Finish</Text>
              <Form.Item name={['results', weldKey, 'vc', 'finish_markings', 'smooth_transition_tack_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Smooth start/stop & tack removal</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'vc', 'finish_markings', 'spatter_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Acceptable spatter (if any)</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="start" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Remarks</Text>
              <Form.Item name={["results", weldKey, "vc", "remarks"]} style={{ margin: 0, flex: 1 }}>
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} style={{ minWidth: 480 }} disabled={isView} />
              </Form.Item>
              {!isView && (
                <CheckAllButtons
                  onSetAll={(v) => {
                    const k = weldKey;
                    form.setFieldsValue({
                      results: {
                        [k]: {
                          vc: {
                            external_profile_dimensions: {
                              weld_size_ok: v,
                              reinforcement_ok: v,
                              contour_ok: v,
                              undersize_oversize_ok: v,
                            },
                            surface_discontinuities: {
                              no_cracks: v,
                              no_porosity: v,
                              undercut_ok: v,
                              no_overlap: v,
                              fusion_fill_ok: v,
                            },
                            root_penetration: {
                              root_condition_ok: v,
                              penetration_ok: v,
                              root_reinforcement_ok: v,
                            },
                            cleanliness_contaminants: {
                              slag_spatter_removed: v,
                              no_foreign_inclusions: v,
                              toes_adjacent_clean: v,
                            },
                            haz_thermal_effects: {
                              discoloration_ok: v,
                              no_haz_cracking: v,
                              coating_damage_checked: v,
                            },
                            distortion_alignment: {
                              misalignment_ok: v,
                              no_twist_bow: v,
                              dimensional_checks_ok: v,
                            },
                            finish_markings: {
                              smooth_transition_tack_ok: v,
                              spatter_ok: v,
                            },
                          },
                        },
                      },
                    });
                  }}
                />
              )}
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
}
